import { createFileRoute } from "@tanstack/react-router";
import { UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  UserDto,
  RoleDto,
  GetUsersResponse,
  CreateUserResponse,
  ApiError,
  OrgDimensionDto,
  UpdateUserRequest,
} from "@procureflow/shared-types";
import { PageHeader } from "@/components/page-header";
import { AddUserDialog, type CreateUserPayload } from "@/components/add-user-dialog";
import { EditUserDialog } from "@/components/edit-user-dialog";
import { UserAvatar } from "@/components/user-avatar";
import { UserRowActions } from "@/components/user-row-actions";
import { fetchWithAuth } from "@/lib/auth-client";
import { StatusBadge } from "@/components/status-badge";
import type { UserActionKind } from "@/lib/user-actions";

// US-055 — Admin creates user accounts directly (replaces the US-002
// invite flow) and manages them (deactivate, reactivate, reset password).
//
// Live fetch from GET /api/users + Add-User modal (role dropdown sourced
// from GET /api/roles, ADR-017) + per-row action buttons. Each action
// POSTs the matching endpoint and updates the row optimistically.

export const Route = createFileRoute("/users")({
  head: () => ({
    meta: [
      { title: "User Management — ProcureFlow" },
      {
        name: "description",
        content: "Manage users, roles and permissions across the platform.",
      },
    ],
  }),
  component: Page,
});

function Page() {
  const [users, setUsers] = useState<UserDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [roleNames, setRoleNames] = useState<string[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState<string | undefined>(undefined);
  // US-055 — per-row state. `pendingId` disables that row's action
  // buttons while a request is in flight; `rowMessage` surfaces the
  // transient success/error line under the row's name.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [rowMessage, setRowMessage] = useState<Record<string, string>>({});
  // US-079 — Edit-User dialog: active org-dim options + the in-flight target.
  const [locations, setLocations] = useState<OrgDimensionDto[]>([]);
  const [departments, setDepartments] = useState<OrgDimensionDto[]>([]);
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | undefined>(undefined);

  async function load() {
    setLoadError(null);
    try {
      const res = await fetchWithAuth("/api/users?limit=100");
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        setLoadError(body?.message ?? "Failed to load users.");
        return;
      }
      const { data } = (await res.json()) as GetUsersResponse;
      setUsers(data);
    } catch {
      setLoadError("Failed to load users.");
    }
  }

  // Role dropdown options (ADR-017): all active role names, System Admin
  // included. Best-effort — a failure leaves the dropdown empty and the
  // server still validates on submit.
  async function loadRoles() {
    try {
      const res = await fetchWithAuth("/api/roles");
      if (!res.ok) return;
      const { data } = (await res.json()) as { data: RoleDto[] };
      setRoleNames(data.map((r) => r.name));
    } catch {
      /* non-fatal */
    }
  }

  // Active Location/Department options for the Edit dialog dropdowns. Best-effort.
  async function loadOrgDims() {
    try {
      const [locRes, depRes] = await Promise.all([
        fetchWithAuth("/api/locations?active_only=true"),
        fetchWithAuth("/api/departments?active_only=true"),
      ]);
      if (locRes.ok) setLocations(((await locRes.json()) as { data: OrgDimensionDto[] }).data);
      if (depRes.ok) setDepartments(((await depRes.json()) as { data: OrgDimensionDto[] }).data);
    } catch {
      /* non-fatal — dropdowns stay empty; the server still validates on save */
    }
  }

  useEffect(() => {
    void load();
    void loadRoles();
    void loadOrgDims();
  }, []);

  async function handleEdit(payload: UpdateUserRequest) {
    if (!editing) return;
    setEditSubmitting(true);
    setEditError(undefined);
    try {
      const res = await fetchWithAuth(`/api/users/${editing.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        setEditError(body?.message ?? "Failed to save changes.");
        return;
      }
      const { data } = (await res.json()) as { data: UserDto };
      setUsers((prev) => prev?.map((u) => (u.id === data.id ? data : u)) ?? prev);
      setRowMessage((m) => ({ ...m, [data.id]: "Saved." }));
      setEditing(null);
    } catch {
      setEditError("An unexpected error occurred.");
    } finally {
      setEditSubmitting(false);
    }
  }

  async function handleCreate(payload: CreateUserPayload) {
    setAddSubmitting(true);
    setAddError(undefined);
    try {
      const res = await fetchWithAuth("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.status !== 201) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        setAddError(body?.message ?? "Failed to create user.");
        return;
      }
      const { data } = (await res.json()) as CreateUserResponse;
      setUsers((prev) => (prev ? [data, ...prev] : [data]));
      setAddOpen(false);
    } catch {
      setAddError("An unexpected error occurred.");
    } finally {
      setAddSubmitting(false);
    }
  }

  // US-055 — single dispatcher for the per-row actions. Each endpoint
  // returns the mutated UserDto; we update the local row accordingly.
  async function handleRowAction(user: UserDto, action: UserActionKind) {
    if (action === "edit") {
      // Open the edit dialog — no request until the admin saves.
      setEditError(undefined);
      setEditing(user);
      return;
    }
    setPendingId(user.id);
    setRowMessage((m) => ({ ...m, [user.id]: "" }));
    try {
      const path =
        action === "deactivate"
          ? `/api/users/${user.id}/deactivate`
          : action === "reactivate"
            ? `/api/users/${user.id}/reactivate`
            : `/api/users/${user.id}/reset-password`;
      const res = await fetchWithAuth(path, { method: "POST" });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        setRowMessage((m) => ({ ...m, [user.id]: body?.message ?? "Action failed." }));
        return;
      }
      if (action === "deactivate") {
        setUsers(
          (prev) =>
            prev?.map((u) => (u.id === user.id ? { ...u, status: "Deactivated" } : u)) ?? prev,
        );
        setRowMessage((m) => ({ ...m, [user.id]: "Deactivated." }));
      } else if (action === "reactivate") {
        setUsers(
          (prev) => prev?.map((u) => (u.id === user.id ? { ...u, status: "Active" } : u)) ?? prev,
        );
        setRowMessage((m) => ({ ...m, [user.id]: "Reactivated." }));
      } else if (action === "reset_password") {
        setRowMessage((m) => ({ ...m, [user.id]: "Password reset email sent." }));
      }
    } catch {
      setRowMessage((m) => ({ ...m, [user.id]: "An unexpected error occurred." }));
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        description="Role-based access control for your procurement organization."
        action={
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-3.5 py-2 text-sm font-medium text-primary-foreground shadow-glow"
          >
            <UserPlus className="h-4 w-4" /> Add user
          </button>
        }
      />

      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        {loadError && (
          <div role="alert" className="border-b border-border px-5 py-3 text-sm text-destructive">
            {loadError}
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="text-left font-medium px-5 py-3">User</th>
              <th className="text-left font-medium px-3 py-3">Role</th>
              <th className="text-left font-medium px-5 py-3">Status</th>
              <th className="text-right font-medium px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users === null && !loadError && (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-sm text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {users && users.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-sm text-muted-foreground">
                  No users yet. Add a user to get started.
                </td>
              </tr>
            )}
            {users?.map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-accent/40">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <UserAvatar userId={u.id} name={u.name || u.email} size={36} />
                    <div>
                      <p className="font-medium text-foreground">{u.name || u.email}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                      {rowMessage[u.id] && (
                        <p className="mt-0.5 text-xs text-muted-foreground">{rowMessage[u.id]}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3.5 text-foreground">{u.role}</td>
                <td className="px-5 py-3.5">
                  <StatusBadge status={u.status.toLowerCase()} />
                </td>
                <td className="px-5 py-3.5 text-right">
                  <div className="flex justify-end">
                    <UserRowActions
                      status={u.status}
                      onAction={(action) => void handleRowAction(u, action)}
                      pending={pendingId === u.id}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddUserDialog
        open={addOpen}
        onOpenChange={(open) => {
          setAddOpen(open);
          if (!open) setAddError(undefined);
        }}
        onSubmit={handleCreate}
        submitting={addSubmitting}
        roles={roleNames}
        error={addError}
      />

      {editing && (
        <EditUserDialog
          open={editing !== null}
          onOpenChange={(open) => {
            if (!open) {
              setEditing(null);
              setEditError(undefined);
            }
          }}
          onSubmit={handleEdit}
          submitting={editSubmitting}
          user={editing}
          locations={locations}
          departments={departments}
          error={editError}
        />
      )}
    </div>
  );
}
